import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { apiClient } from '../api/apiClient';

const AuthContext = createContext(null);

// Ohne Zeitlimit würde eine Anfrage, die serverseitig einfach nie antwortet (offene
// Verbindung ohne Antwort), die Prüfung für immer offen halten — kein `then`, kein `catch`,
// kein `finally` läuft je, `authChecking` bliebe dauerhaft `true` und der Nutzer sähe endlos
// den Ladehinweis. Vor diesem Task hätte er die App wenigstens aus dem localStorage-Stand
// gesehen. 5s liegen im Alltag deutlich über einer normalen Antwortzeit (fallen also nicht
// auf), sind aber kurz genug, dass ein echter Hänger nicht zur Dauer-Ladeanzeige wird.
export const AUTH_CHECK_TIMEOUT_MS = 5000;

/** Lässt `promise` nach `ms` mit einem Fehler ohne `status` auflaufen, falls es bis dahin
 * selbst nicht ausgelöst hat — ein solcher Fehler zählt unten wie ein Netzwerkfehler: der
 * gespeicherte Stand bleibt stehen, es wird nicht abgemeldet. */
function mitZeitlimit(promise, ms) {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Zeitüberschreitung bei der Anmeldeprüfung.')), ms);
    }),
  ]);
}

export function AuthProvider({ children }) {
  // Träge aus localStorage initialisiert: Besucher ohne Token sollen den Ladehinweis gar
  // nicht erst sehen (auch nicht für einen Frame) — mit `useState(true)` liefe der Effekt
  // erst nach dem ersten Paint und „Laden..." würde kurz aufblitzen, bevor die Anmeldeseite
  // erscheint.
  const [authChecking, setAuthChecking] = useState(
    () => !!(localStorage.getItem('authToken') && localStorage.getItem('user')),
  );
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  // React.StrictMode führt Effekte in der Entwicklung zweimal aus (mount → cleanup →
  // erneuter mount); der Ref überlebt das, weil dieselbe Komponenteninstanz beide
  // Durchläufe teilt. Ohne ihn würde die Startprüfung serverseitig doppelt ankommen —
  // das lässt sich clientseitig per AbortController nicht verhindern, weil die Anfrage
  // beim zweiten Durchlauf längst unterwegs zum Server ist. Trägt nur, solange die
  // Abhängigkeitsliste des Effekts leer bleibt — mit echten Abhängigkeiten liefe der Effekt
  // wiederholt und der Ref müsste pro Lauf zurückgesetzt werden, sonst bliebe er für immer
  // beim ersten Ergebnis stehen.
  const authCheckRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('user');
    if (!token || !userData) {
      setAuthChecking(false);
      return;
    }

    let gespeicherterNutzer = null;
    try {
      gespeicherterNutzer = JSON.parse(userData);
    } catch (err) {
      console.error('Fehler beim Parsen der Benutzer-Daten:', err);
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      setAuthChecking(false);
      return;
    }

    // Zunächst den gespeicherten Stand übernehmen, damit nach der Prüfung kein zweiter
    // Renderdurchlauf mit leerem Nutzer entsteht.
    setAuthToken(token);
    setCurrentUser(gespeicherterNutzer);
    setIsAuthenticated(true);

    let abgebrochen = false;
    // Die Anfrage nur beim ersten der beiden StrictMode-Durchläufe stellen; der zweite
    // hängt sich mit eigenem `abgebrochen`-Flag an dasselbe Promise — dessen Ergebnis
    // wendet dann der Durchlauf an, der nicht per Cleanup abgebrochen wurde.
    if (!authCheckRef.current) {
      authCheckRef.current = mitZeitlimit(apiClient.get('/api/auth/me'), AUTH_CHECK_TIMEOUT_MS);
    }
    authCheckRef.current
      .then(({ data }) => {
        if (!abgebrochen) login(data.user, token);
      })
      .catch((err) => {
        // Nur eine echte 401 heißt "Token gilt nicht mehr" — bei Netzwerk-, Server- oder
        // Zeitüberschreitungsfehlern (kein `status`, siehe `mitZeitlimit`) bleibt der
        // gespeicherte Stand bestehen, sonst fliegt man bei jedem Hänger raus.
        if (!abgebrochen && err?.status === 401) logout();
      })
      .finally(() => {
        if (!abgebrochen) setAuthChecking(false);
      });

    return () => { abgebrochen = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = (user, token) => {
    setCurrentUser(user);
    setAuthToken(token);
    setIsAuthenticated(true);
    try {
      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(user));
    } catch {}
  };

  const logout = () => {
    try {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
    } catch {}
    setCurrentUser(null);
    setAuthToken(null);
    setIsAuthenticated(false);
  };

  const isAdmin = currentUser?.role === 'admin';
  const isPro = ['pro', 'admin'].includes(currentUser?.role);

  const value = useMemo(
    () => ({ isAuthenticated, currentUser, authToken, authChecking, isAdmin, isPro, login, logout }),
    [isAuthenticated, currentUser, authToken, authChecking, isAdmin, isPro],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
